// Procedurally generated texture atlas. Drawing tiles on a canvas keeps the
// game fully self-contained (no external asset files / no network fetches).

import * as THREE from 'three';
import { Block } from '../sim/blocks';

export const TILE = 16; // pixels per tile
export const ATLAS_COLS = 4;
export const ATLAS_ROWS = 4;

// Tile indices within the atlas.
export const enum Tile {
  GrassTop = 0,
  GrassSide = 1,
  Dirt = 2,
  Stone = 3,
  Sand = 4,
  Water = 5,
  WoodTop = 6,
  WoodSide = 7,
  Leaves = 8,
  Planks = 9,
  Cobblestone = 10,
  Torch = 11,
  Bedrock = 12,
  CraftingTableTop = 13,
  CraftingTableSide = 14,
}

export interface BlockTiles {
  top: Tile;
  bottom: Tile;
  side: Tile;
}

export function blockTiles(block: Block): BlockTiles {
  switch (block) {
    case Block.Grass:
      return { top: Tile.GrassTop, bottom: Tile.Dirt, side: Tile.GrassSide };
    case Block.Dirt:
      return { top: Tile.Dirt, bottom: Tile.Dirt, side: Tile.Dirt };
    case Block.Stone:
      return { top: Tile.Stone, bottom: Tile.Stone, side: Tile.Stone };
    case Block.Sand:
      return { top: Tile.Sand, bottom: Tile.Sand, side: Tile.Sand };
    case Block.Water:
      return { top: Tile.Water, bottom: Tile.Water, side: Tile.Water };
    case Block.Wood:
      return { top: Tile.WoodTop, bottom: Tile.WoodTop, side: Tile.WoodSide };
    case Block.Leaves:
      return { top: Tile.Leaves, bottom: Tile.Leaves, side: Tile.Leaves };
    case Block.Planks:
      return { top: Tile.Planks, bottom: Tile.Planks, side: Tile.Planks };
    case Block.Cobblestone:
      return { top: Tile.Cobblestone, bottom: Tile.Cobblestone, side: Tile.Cobblestone };
    case Block.Torch:
      return { top: Tile.Torch, bottom: Tile.Torch, side: Tile.Torch };
    case Block.Bedrock:
      return { top: Tile.Bedrock, bottom: Tile.Bedrock, side: Tile.Bedrock };
    case Block.CraftingTable:
      return { top: Tile.CraftingTableTop, bottom: Tile.Planks, side: Tile.CraftingTableSide };
    default:
      return { top: Tile.Stone, bottom: Tile.Stone, side: Tile.Stone };
  }
}

/** UV rect (u0,v0,u1,v1) for a tile, with a tiny inset to avoid bleeding. */
export function tileUV(tile: Tile): [number, number, number, number] {
  const col = tile % ATLAS_COLS;
  const row = Math.floor(tile / ATLAS_COLS);
  const inset = 0.001;
  const u0 = col / ATLAS_COLS + inset;
  const u1 = (col + 1) / ATLAS_COLS - inset;
  // Canvas Y is top-down; flip so row 0 is at the top of the texture.
  const v1 = 1 - row / ATLAS_ROWS - inset;
  const v0 = 1 - (row + 1) / ATLAS_ROWS + inset;
  return [u0, v0, u1, v1];
}

type RGB = [number, number, number];

function jitter([r, g, b]: RGB, amount: number, rand: () => number): string {
  const d = (rand() - 0.5) * amount;
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v + d)));
  return `rgb(${c(r)},${c(g)},${c(b)})`;
}

// A tiny local PRNG so texture generation is deterministic.
function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function drawTile(
  ctx: CanvasRenderingContext2D,
  tile: Tile,
  base: RGB,
  opts: { speckle?: number; seed: number },
): void {
  const col = tile % ATLAS_COLS;
  const row = Math.floor(tile / ATLAS_COLS);
  const ox = col * TILE;
  const oy = row * TILE;
  const rand = prng(opts.seed);
  const speckle = opts.speckle ?? 18;
  for (let y = 0; y < TILE; y++) {
    for (let x = 0; x < TILE; x++) {
      ctx.fillStyle = jitter(base, speckle, rand);
      ctx.fillRect(ox + x, oy + y, 1, 1);
    }
  }
}

/** Build the atlas texture once at startup. */
export function createAtlasTexture(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_COLS * TILE;
  canvas.height = ATLAS_ROWS * TILE;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  drawTile(ctx, Tile.GrassTop, [95, 159, 53], { seed: 1, speckle: 22 });
  drawTile(ctx, Tile.GrassSide, [120, 90, 55], { seed: 2 });
  drawTile(ctx, Tile.Dirt, [120, 85, 58], { seed: 3 });
  drawTile(ctx, Tile.Stone, [128, 128, 132], { seed: 4, speckle: 26 });
  drawTile(ctx, Tile.Sand, [219, 205, 150], { seed: 5, speckle: 14 });
  drawTile(ctx, Tile.Water, [55, 110, 200], { seed: 6, speckle: 20 });
  drawTile(ctx, Tile.WoodTop, [160, 128, 82], { seed: 7, speckle: 18 });
  drawTile(ctx, Tile.WoodSide, [110, 82, 50], { seed: 8, speckle: 20 });
  drawTile(ctx, Tile.Leaves, [58, 120, 46], { seed: 9, speckle: 34 });
  drawTile(ctx, Tile.Planks, [168, 132, 78], { seed: 10, speckle: 14 });
  drawTile(ctx, Tile.Cobblestone, [110, 110, 114], { seed: 11, speckle: 40 });
  drawTile(ctx, Tile.Torch, [230, 190, 70], { seed: 12, speckle: 30 });
  drawTile(ctx, Tile.Bedrock, [40, 40, 44], { seed: 13, speckle: 30 });
  drawTile(ctx, Tile.CraftingTableTop, [150, 116, 70], { seed: 14, speckle: 22 });
  drawTile(ctx, Tile.CraftingTableSide, [126, 96, 56], { seed: 15, speckle: 20 });

  // Crafting-table top: a darker grid so it reads as a work surface.
  const cx = (Tile.CraftingTableTop % ATLAS_COLS) * TILE;
  const cy = Math.floor(Tile.CraftingTableTop / ATLAS_COLS) * TILE;
  ctx.fillStyle = 'rgba(60,44,26,0.9)';
  for (let i = 0; i < TILE; i++) {
    ctx.fillRect(cx + i, cy + Math.floor(TILE / 2), 1, 1);
    ctx.fillRect(cx + Math.floor(TILE / 2), cy + i, 1, 1);
  }

  // Grass side: draw a green top strip so the side reads as grass-over-dirt.
  const gx = (Tile.GrassSide % ATLAS_COLS) * TILE;
  const gy = Math.floor(Tile.GrassSide / ATLAS_COLS) * TILE;
  const grand = prng(99);
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < TILE; x++) {
      ctx.fillStyle = jitter([95, 159, 53], 22, grand);
      ctx.fillRect(gx + x, gy + y, 1, 1);
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestMipmapNearestFilter;
  texture.generateMipmaps = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
